import type { MouseEvent, ReactNode } from "react";
import { Check, Copy, FileCode2, Link2, TriangleAlert } from "lucide-react";
import { useToast } from "@components/ui/use-toast";
import { matchesShowcaseQuery, useShowcaseSearchQuery } from "@shared-kernel/primary/shared/design-system/shared/search-context";

export interface ShowcaseBlockProps {
  title: string;
  description?: string;
  id?: string;
  importPath?: string;
  sourcePath?: string;
  useWhen?: string;
  avoidWhen?: string;
  children: ReactNode;
}

export const ShowcaseBlock = ({ title, description, id, importPath, sourcePath, useWhen, avoidWhen, children }: ShowcaseBlockProps) => {
  const { toast } = useToast();
  const searchQuery = useShowcaseSearchQuery();

  if (!matchesShowcaseQuery(title, searchQuery)) return null;

  const handleAnchorClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!id) return;
    event.preventDefault();
    const url = `${window.location.origin}${window.location.pathname}#${id}`;
    window.history.replaceState(null, "", `#${id}`);
    void navigator.clipboard.writeText(url).then(
      () => toast({ description: "Lien copié dans le presse-papiers." }),
      () => toast({ variant: "destructive", description: "Impossible de copier le lien." }),
    );
  };

  const handleImportCopy = () => {
    if (!importPath) return;
    void navigator.clipboard.writeText(importPath).then(
      () => toast({ description: "Copié." }),
      () => toast({ variant: "destructive", description: "Impossible de copier l'import." }),
    );
  };

  const handleSourceCopy = () => {
    if (!sourcePath) return;
    void navigator.clipboard.writeText(sourcePath).then(
      () => toast({ description: "Chemin copié." }),
      () => toast({ variant: "destructive", description: "Impossible de copier le chemin." }),
    );
  };

  return (
    <section id={id} className="group/showcase scroll-mt-20 space-y-3">
      <header className="space-y-1.5">
        <h3 className="flex items-center gap-1.5 text-base font-semibold tracking-tight">
          {title}
          {id && (
            <a
              href={`#${id}`}
              onClick={handleAnchorClick}
              aria-label={`Copier le lien vers ${title}`}
              className="text-muted-foreground opacity-0 transition-opacity hover:text-foreground focus-visible:opacity-100 group-hover/showcase:opacity-100"
            >
              <Link2 className="size-4" />
            </a>
          )}
        </h3>
        {description && <p className="text-sm text-muted-foreground">{description}</p>}
        {(importPath || sourcePath) && (
          <div className="flex max-w-full flex-wrap items-center gap-2">
            {importPath && (
              <button
                type="button"
                onClick={handleImportCopy}
                aria-label={`Copier l'import de ${title}`}
                className="inline-flex max-w-full items-center gap-1.5 rounded border bg-muted px-2 py-1 font-mono text-xs text-muted-foreground transition-colors hover:bg-muted/70 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <Copy className="size-3 shrink-0" />
                <span className="truncate">{importPath}</span>
              </button>
            )}
            {sourcePath && (
              <button
                type="button"
                onClick={handleSourceCopy}
                aria-label={`Copier le chemin source de ${title}`}
                className="inline-flex max-w-full items-center gap-1.5 rounded px-1.5 py-0.5 font-mono text-xs text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <FileCode2 className="size-3 shrink-0" />
                <span className="truncate">{sourcePath}</span>
              </button>
            )}
          </div>
        )}
      </header>
      {(useWhen || avoidWhen) && (
        <div className="space-y-2">
          {useWhen && (
            <div className="flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900 dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-100">
              <Check className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide">À utiliser quand</p>
                <p className="text-sm">{useWhen}</p>
              </div>
            </div>
          )}
          {avoidWhen && (
            <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-100">
              <TriangleAlert className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide">À éviter quand</p>
                <p className="text-sm">{avoidWhen}</p>
              </div>
            </div>
          )}
        </div>
      )}
      <div className="rounded-lg border bg-card p-6">{children}</div>
    </section>
  );
};
